import type { Instant } from '@js-joda/core';
import type { Signal } from '@preact/signals-react';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';

interface UserData {
    user: UserDetails | null | undefined;
    masqUser?: UserDetails;
    isSuperUserModeEnabled: boolean;
    masqExpires?: Instant;
}

export type ActiveUserModelState =
    | {
          type: 'initializing';
      }
    | {
          type: 'initialized';
          userData: UserData;
          roles: string[];
          env: string;
          superUserAlerting: Signal<boolean>;
      };

export type ActiveUserModelAction =
    | {
          type: 'INITIALIZE_SUCCESS';
          userData: UserData;
          roles: string[];
          env: string;
          superUserAlerting: Signal<boolean>;
      }
    | {
          type: 'UPDATE_USER_DATA';
          userData: UserData;
      }
    | {
          type: 'UPDATE_ROLES';
          roles: string[];
      }
    | {
          type: 'UPDATE_SUPER_USER_ALERTING';
          enabled: boolean;
      };

export function activeUserModelReducer(
    state: ActiveUserModelState,
    action: ActiveUserModelAction,
): ActiveUserModelState {
    switch (action.type) {
        case 'INITIALIZE_SUCCESS':
            return {
                type: 'initialized',
                userData: action.userData,
                roles: action.roles,
                env: action.env,
                superUserAlerting: action.superUserAlerting,
            };

        case 'UPDATE_USER_DATA':
            if (state.type === 'initializing') {
                return state;
            }

            // When userData changes (masq or user changes), reset superUserAlerting based on environment and developer status
            const currentIsDeveloper =
                action.userData.masqUser !== undefined
                    ? action.userData.masqUser.isDeveloper
                    : (action.userData.user?.isDeveloper ?? false);

            if (state.env === 'prod') {
                // In production, all users (including developers) have alerting enabled
                state.superUserAlerting.value = true;
            } else {
                // In non-production environments, developers have alerting disabled by default
                state.superUserAlerting.value = !currentIsDeveloper;
            }

            return {
                ...state,
                userData: action.userData,
            };

        case 'UPDATE_ROLES':
            if (state.type === 'initializing') {
                return state;
            }

            return {
                ...state,
                roles: action.roles,
            };

        case 'UPDATE_SUPER_USER_ALERTING':
            if (state.type === 'initializing') {
                return state;
            }

            state.superUserAlerting.value = action.enabled;
            return state;

        default:
            return state;
    }
}
